import {
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import clsx from "clsx";
import { Bars3Icon, ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { Button } from "@/components/ui/Button";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { HomeTileCatalogDialog } from "@/pages/home/HomeTileCatalogDialog";
import {
  assignHomeBoardColumns,
  homeBoardPositions,
  HOME_BOARD_WIDE_MQ,
  parseHomeBoardGap,
} from "@/pages/home/homeBoardLayout";
import {
  HOME_TILE_LABELS,
  type HomeColumnCount,
  type HomeTileId,
  type HomeTileSpan,
} from "@/pages/home/homeLayout";

export interface HomeBoardTile {
  id: HomeTileId;
  span: HomeTileSpan;
  node: ReactNode;
}

const COLUMN_OPTIONS: { value: `${HomeColumnCount}`; label: string }[] = [
  { value: "1", label: "1 Spalte" },
  { value: "2", label: "2 Spalten" },
  { value: "3", label: "3 Spalten" },
];

function spanLabel(span: HomeTileSpan): string {
  return span === 1 ? "1 Spalte" : `${span} Spalten`;
}

const HEIGHT_EPSILON_PX = 1;

type PackState = {
  packed: boolean;
  columnCount: number;
  colWidth: number;
  gap: number;
  heights: number[];
};

function listsEqual(a: number[], b: number[], epsilon: number): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (Math.abs(a[i]! - b[i]!) > epsilon) return false;
  }
  return true;
}

function readHomeBoardGap(root: HTMLElement): number {
  const raw = getComputedStyle(root).getPropertyValue("--eh-home-board-gap");
  const rootFont = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
  return parseHomeBoardGap(raw, rootFont);
}

export function HomeTileBoard({
  tiles,
  arranging,
  columns,
  permittedIds,
  visibleIds,
  saveError,
  onMove,
  onDropOn,
  onColumnsChange,
  onSpanChange,
  onToggleTile,
  onRemove,
}: {
  tiles: HomeBoardTile[];
  arranging: boolean;
  columns: HomeColumnCount;
  permittedIds: HomeTileId[];
  visibleIds: ReadonlySet<HomeTileId>;
  saveError: string | null;
  onMove: (id: HomeTileId, delta: -1 | 1) => void;
  onDropOn: (fromId: HomeTileId, toId: HomeTileId) => void;
  onColumnsChange: (columns: HomeColumnCount) => void;
  onSpanChange: (id: HomeTileId, span: HomeTileSpan) => void;
  onToggleTile: (id: HomeTileId) => void;
  onRemove: (id: HomeTileId) => void;
}) {
  const [overId, setOverId] = useState<HomeTileId | null>(null);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef(new Map<HomeTileId, HTMLDivElement>());
  const [pack, setPack] = useState<PackState>({
    packed: false,
    columnCount: columns,
    colWidth: 0,
    gap: 24,
    heights: [],
  });
  const spanOptions = ([1, 2, 3] as const).filter((span) => span <= columns);
  const tileKey = tiles.map((tile) => `${tile.id}:${tile.span}`).join("|");
  const count = tiles.length;

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || count === 0) return;

    const sync = () => {
      const width = root.clientWidth;
      if (width <= 0) return;
      const wide = window.matchMedia(HOME_BOARD_WIDE_MQ).matches;
      const columnCount = wide ? columns : 1;
      const gap = readHomeBoardGap(root);
      const colWidth = (width - gap * Math.max(0, columnCount - 1)) / columnCount;
      const ids = tileKey
        .split("|")
        .map((part) => part.slice(0, part.lastIndexOf(":")) as HomeTileId);
      const heights: number[] = [];
      for (let i = 0; i < ids.length; i += 1) {
        const el = itemRefs.current.get(ids[i]!);
        if (!el) return;
        heights[i] = el.offsetHeight;
      }

      setPack((prev) => {
        if (
          prev.packed &&
          prev.columnCount === columnCount &&
          Math.abs(prev.colWidth - colWidth) < 0.5 &&
          Math.abs(prev.gap - gap) < 0.5 &&
          listsEqual(prev.heights, heights, HEIGHT_EPSILON_PX)
        ) {
          return prev;
        }
        return { packed: true, columnCount, colWidth, gap, heights };
      });
    };

    sync();

    const ro = new ResizeObserver(() => sync());
    ro.observe(root);
    itemRefs.current.forEach((el) => ro.observe(el));
    const mq = window.matchMedia(HOME_BOARD_WIDE_MQ);
    mq.addEventListener("change", sync);
    return () => {
      ro.disconnect();
      mq.removeEventListener("change", sync);
    };
    // colWidth: zweite Messung, nachdem die absolute Breite den Umbruch geändert hat
  }, [arranging, columns, count, tileKey, pack.columnCount, pack.colWidth, pack.packed]);

  const placements = useMemo(() => {
    const spans =
      tileKey.length === 0 ? [] : tileKey.split("|").map((part) => Number(part.split(":")[1]) || 1);
    return assignHomeBoardColumns(spans, pack.columnCount || columns);
  }, [tileKey, pack.columnCount, columns]);

  const packedLayout = useMemo(
    () => homeBoardPositions(pack.heights, placements, pack.columnCount, pack.colWidth, pack.gap),
    [pack.heights, pack.columnCount, pack.colWidth, pack.gap, placements],
  );
  const ready = pack.packed && pack.colWidth > 0 && pack.heights.length === count;

  return (
    <div className="min-w-0 space-y-4">
      {arranging ? (
        <div className="eh-home-arrange" role="region" aria-label="Startseite anordnen">
          <div className="eh-home-arrange-note" role="status">
            <p>
              Ziehe die Kacheln am Griff oder sortiere sie mit den Pfeilen. Wähle das Spaltenlayout
              und stell je Kachel die Breite ein. Über „Kacheln“ fügst du weitere Bereiche hinzu.
              Die Einstellung wird nur für dich gespeichert.
            </p>
            <p className="mt-1">
              Auf schmalen Bildschirmen stehen die Kacheln untereinander. In mehreren Spalten rückt
              die nächste Kachel einer Spalte direkt darunter, die gespeicherte Reihenfolge bleibt.
            </p>
            {saveError ? <p className="mt-1 text-status-red">{saveError}</p> : null}
          </div>
          <div className="eh-home-arrange__row">
            <span className="eh-home-arrange__label" id="home-columns-label">
              Spalten
            </span>
            <SegmentedControl
              name="Spaltenlayout"
              value={String(columns) as `${HomeColumnCount}`}
              options={COLUMN_OPTIONS}
              onChange={(value) => onColumnsChange(Number(value) as HomeColumnCount)}
            />
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => setCatalogOpen(true)}
            >
              Kacheln
            </Button>
          </div>
          <HomeTileCatalogDialog
            open={catalogOpen}
            onClose={() => setCatalogOpen(false)}
            permittedIds={permittedIds}
            visibleIds={visibleIds}
            onToggleTile={onToggleTile}
          />
        </div>
      ) : null}
      {tiles.length === 0 ? (
        <div className="eh-home-arrange-note" role="status">
          <p>
            {arranging
              ? "Noch keine Kachel ausgewählt. Über „Kacheln“ kannst du welche hinzufügen."
              : "Keine Kacheln auf der Startseite. Über Anordnen kannst du welche hinzufügen."}
          </p>
        </div>
      ) : (
        <div
          ref={rootRef}
          className={clsx("eh-home-board", `eh-home-board--cols-${columns}`)}
          data-packed={ready ? "true" : "false"}
          style={ready ? { height: packedLayout.totalHeight } : undefined}
        >
          {tiles.map((tile, index) => {
            const pos = packedLayout.positions[index];
            const style: CSSProperties | undefined =
              ready && pos?.visible
                ? {
                    width: pos.width,
                    transform: `translate(${pos.left}px, ${pos.top}px)`,
                  }
                : undefined;
            return (
              <div
                key={tile.id}
                ref={(el) => {
                  if (el) itemRefs.current.set(tile.id, el);
                  else itemRefs.current.delete(tile.id);
                }}
                className={clsx(
                  "eh-home-tile",
                  tile.span > 1 && `eh-home-tile--span-${tile.span}`,
                  arranging && "eh-home-tile--arranging",
                  arranging && overId === tile.id && "eh-home-tile--drop",
                )}
                style={style}
                onDragOver={(event) => {
                  if (!arranging) return;
                  event.preventDefault();
                  setOverId(tile.id);
                }}
                onDragLeave={() => setOverId((current) => (current === tile.id ? null : current))}
                onDrop={(event) => {
                  if (!arranging) return;
                  event.preventDefault();
                  const fromId = event.dataTransfer.getData("text/plain");
                  setOverId(null);
                  if (fromId) onDropOn(fromId as HomeTileId, tile.id);
                }}
              >
                {arranging ? (
                  <div className="eh-home-tile__bar">
                    <button
                      type="button"
                      className="eh-home-tile__handle"
                      draggable
                      aria-label={`${HOME_TILE_LABELS[tile.id]} ziehen`}
                      onDragStart={(event) => {
                        event.dataTransfer.setData("text/plain", tile.id);
                        event.dataTransfer.effectAllowed = "move";
                      }}
                      onDragEnd={() => setOverId(null)}
                    >
                      <Bars3Icon aria-hidden="true" />
                      Ziehen
                    </button>
                    <button
                      type="button"
                      className="eh-home-tile__nudge"
                      aria-label="Nach oben"
                      disabled={index === 0}
                      onClick={() => onMove(tile.id, -1)}
                    >
                      <ChevronUpIcon aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      className="eh-home-tile__nudge"
                      aria-label="Nach unten"
                      disabled={index === tiles.length - 1}
                      onClick={() => onMove(tile.id, 1)}
                    >
                      <ChevronDownIcon aria-hidden="true" />
                    </button>
                    {spanOptions.length > 1 ? (
                      <div className="eh-home-tile__width">
                        <span className="eh-home-tile__width-label">Breite</span>
                        <SegmentedControl
                          name={`Breite ${HOME_TILE_LABELS[tile.id]}`}
                          value={String(tile.span) as `${HomeTileSpan}`}
                          options={spanOptions.map((span) => ({
                            value: String(span) as `${HomeTileSpan}`,
                            label: spanLabel(span),
                          }))}
                          onChange={(value) => onSpanChange(tile.id, Number(value) as HomeTileSpan)}
                        />
                      </div>
                    ) : null}
                    <button
                      type="button"
                      className="eh-home-tile__remove"
                      onClick={() => onRemove(tile.id)}
                    >
                      Entfernen
                    </button>
                  </div>
                ) : null}
                {tile.node}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
